import { HDate, HebrewCalendar, flags } from '@hebcal/core';

/**
 * Calculate initial angle from today's Hebrew date
 * Angle 0 = 1 Tishrei, angle increases clockwise
 */
export function calculateInitialAngle() {
  try {
    // Get today's Hebrew date
    const today = new HDate();
    
    // Get Hebrew month and day
    // HDate.getMonth() returns 1-12, where 1=Nisan, 7=Tishrei, 10=Tevet, 11=Shvat
    const hebrewMonth = today.getMonth(); // 1-12
    const hebrewDay = today.getDate();
    const daysInMonth = today.daysInMonth();
    
    // Map Hebrew month (1-12, 1=Nisan) to our array index (0-11, 0=Tishrei)
    const monthMap = {
      7: 0,   // Tishrei
      8: 1,   // Cheshvan
      9: 2,   // Kislev
      10: 3,  // Tevet
      11: 4,  // Shvat
      12: 5,  // Adar
      1: 6,   // Nisan
      2: 7,   // Iyar
      3: 8,   // Sivan
      4: 9,   // Tammuz
      5: 10,  // Av
      6: 11   // Elul
    };
    
    const monthIndex = monthMap[hebrewMonth];
    if (monthIndex === undefined) {
      console.error('Unknown month:', hebrewMonth, 'for date:', today.toString());
      return 0;
    }
    
    // Calculate angle
    // Each month = 30 degrees
    // Day within month = proportional angle within that 30 degrees
    const monthAngle = monthIndex * 30;
    const dayFraction = (hebrewDay - 1) / daysInMonth; // 0 to ~1
    const dayAngle = dayFraction * 30;
    
    return normalizeAngle(monthAngle + dayAngle);
  } catch (error) {
    console.error('Error calculating initial angle:', error);
    return 0;
  }
}

/**
 * Normalize angle to 0-360 range
 */
export function normalizeAngle(angle) {
  let normalized = angle % 360;
  if (normalized < 0) normalized += 360;
  return normalized;
}

/**
 * Get current Hebrew date from angle
 */
export function getCurrentHebrewDate(angle) {
  try {
    const normalized = normalizeAngle(angle);
    
    // Get current Hebrew year (should stay constant as we scrub through months)
    const today = new HDate();
    const hebrewYear = today.getFullYear();
    
    // Calculate month index from angle
    // Our array: Tishrei(0), Cheshvan(1), Kislev(2), Tevet(3), Shvat(4), Adar(5), Nisan(6), Iyar(7), Sivan(8), Tammuz(9), Av(10), Elul(11)
    const monthIndex = Math.floor(normalized / 30) % 12;
    
    // Map our array index back to Hebrew month number (1-indexed for HDate)
    const reverseMonthMap = {
      0: 7,   // Tishrei -> 7
      1: 8,   // Cheshvan -> 8
      2: 9,   // Kislev -> 9
      3: 10,  // Tevet -> 10
      4: 11,  // Shvat -> 11
      5: 12,  // Adar -> 12
      6: 1,   // Nisan -> 1
      7: 2,   // Iyar -> 2
      8: 3,   // Sivan -> 3
        9: 4,   // Tammuz -> 4
      10: 5,  // Av -> 5
      11: 6   // Elul -> 6
    };
    
    const hebrewMonth = reverseMonthMap[monthIndex];
    if (hebrewMonth === undefined) {
      console.error('Invalid monthIndex:', monthIndex);
      return { hebrew: '', english: '', hDate: null, gematria: '' };
    }
    
    // Create HDate for first day of this month to get daysInMonth
    // HDate constructor: new HDate(year, month, day) where month is 1-12
    let monthStart;
    try {
      monthStart = new HDate(hebrewYear, hebrewMonth, 1);
    } catch (e) {
      console.error('Error creating month start:', e, { hebrewYear, hebrewMonth });
      const today = new HDate();
      monthStart = new HDate(today.getFullYear(), hebrewMonth, 1);
    }
    const daysInMonth = monthStart.daysInMonth();
    
    // Calculate day within month from angle
    // The angle calculation uses: dayFraction = (hebrewDay - 1) / daysInMonth, then dayAngle = dayFraction * 30
    // So to reverse: dayFraction = (normalized % 30) / 30, then dayInMonth = dayFraction * daysInMonth + 1
    const angleInMonth = normalized % 30; // 0-30 degrees within the month
    const dayFraction = angleInMonth / 30; // 0 to 1
    // Calculate day: dayFraction * daysInMonth gives us the day offset from 1
    // Add 1 to get the actual day (since day 1 = 0 in the fraction)
    // Use Math.floor and add 1 to match the forward calculation exactly
    const dayInMonth = Math.max(1, Math.min(daysInMonth, Math.floor(dayFraction * daysInMonth) + 1));
    
    // Create the actual Hebrew date
    // HDate constructor: new HDate(year, month, day)
    let hDate;
    try {
      hDate = new HDate(hebrewYear, hebrewMonth, dayInMonth);
    } catch (e) {
      console.error('Error creating Hebrew date:', e, { hebrewYear, hebrewMonth, dayInMonth });
      const today = new HDate();
      hDate = new HDate(today.getFullYear(), hebrewMonth, dayInMonth);
    }
    
    // Get Hebrew formatting
    const hebrewMonthName = hDate.getMonthName('h');
    const formattedHebrew = `${dayInMonth} ${hebrewMonthName}`;
    
    // Convert to Gregorian CORRECTLY using HDate's built-in conversion
    const gregorianDate = hDate.greg();
    const formattedEnglish = gregorianDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
    
    // Calculate gematria for the day
    const gematria = calculateGematria(dayInMonth);
    
    return { 
      hebrew: formattedHebrew, 
      english: formattedEnglish,
      hDate: hDate,
      gematria: gematria
    };
  } catch (error) {
    console.error('Error calculating Hebrew date:', error);
    return { hebrew: '', english: '', hDate: null, gematria: '' };
  }
}

/**
 * Calculate gematria (Hebrew letters) for a day number
 * Uses the same logic as numberToHebrewLetters from hebrewCalendar.js
 */
export function calculateGematria(day) {
  if (!day || day <= 0) return '';
  
  const letters = {
    1: 'א', 2: 'ב', 3: 'ג', 4: 'ד', 5: 'ה',
    6: 'ו', 7: 'ז', 8: 'ח', 9: 'ט', 10: 'י',
    20: 'כ', 30: 'ל', 40: 'מ', 50: 'נ', 60: 'ס',
    70: 'ע', 80: 'פ', 90: 'צ', 100: 'ק', 200: 'ר',
    300: 'ש', 400: 'ת'
  };
  
  // For numbers 15 and 16, use טו and טז (to avoid spelling God's name)
  if (day === 15) return 'טו';
  if (day === 16) return 'טז';
  
  if (day <= 10) {
    return letters[day] || '';
  }
  
  // Handle 11-19 specially
  if (day > 10 && day < 20) {
    if (day === 15) return 'טו';
    if (day === 16) return 'טז';
    const ones = day % 10;
    return 'י' + (letters[ones] || '');
  }
  
  // Handle 20-29
  if (day >= 20 && day < 30) {
    const tens = 20;
    const ones = day % 10;
    
    if (ones > 0) {
      // Both tens and ones - geresh goes between them
      return letters[tens] + '״' + letters[ones];
    } else {
      // Only tens - geresh comes before it
      return '״' + letters[tens];
    }
  }
  
  // For days 30+, use the full numberToHebrewLetters logic
  // But for calendar days, we typically only see 1-29
  return day.toString();
}

/**
 * Get next upcoming holiday from a given Hebrew date
 */
export function getNextHoliday(hDate) {
  if (!hDate) return null;
  
  try {
    const hebrewYear = hDate.getFullYear();
    
    // Get calendar events for the current Hebrew year
    const events = HebrewCalendar.calendar({
      year: hebrewYear,
      isHebrewYear: true,
    });
    
    const currentGreg = hDate.greg();
    const now = new Date();
    const referenceDate = currentGreg > now ? currentGreg : now;
    
    // Find next holiday after reference date
    for (const event of events) {
      const eventDate = event.getDate().greg();
      
      if (eventDate > referenceDate) {
        const isRoshChodesh = (event.getFlags() & flags.ROSH_CHODESH) !== 0;
        const isMajorHoliday = (event.getFlags() & flags.MAJOR_FAST) !== 0 || 
                              (event.getFlags() & flags.MAJOR_HOLIDAY) !== 0;
        
        // Skip Rosh Chodesh, prioritize major holidays
        if (isRoshChodesh) continue;
        
        const daysUntil = Math.ceil((eventDate - referenceDate) / (1000 * 60 * 60 * 24));
        
        return {
          name: event.render('en'),
          date: eventDate,
          daysUntil: daysUntil,
          isMajorHoliday: isMajorHoliday,
          hebrewDate: event.getDate().toString('h'),
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting next holiday:', error);
    return null;
  }
}
